<template>
  <div class="v-chart" ref="chartdiv"></div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import moment from "moment";

// amchart assets
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

import {
  getMaxValueFromArrayByKey,
  getMinValueFromArrayByKey,
  capitalizeFirstLetter
} from "@/utils/util";
import variables from "@/styles/_variables.scss";

am4core.useTheme(am4themes_animated);
am4core.options.commercialLicense = true;

const LABELS_MAP = {
  "b&w-usps-first-class": "B&W 1st Class",
  "color-usps-first-class": "Color 1st Class",
  "4x6-usps-first-class": "4x6 1st Class",
  "4x6-usps-standard": "4x6 Standard",
  "6x9-usps-first-class": "6x9 1st Class",
  "6x11-usps-first-class": "6x11 1st Class",
  "b&w-usps-standard": "B&W Standard",
  "color-usps-standard": "Color Standard",
  "6x9-usps-standard": "6x9 Standard",
  "6x11-usps-standard": "6x11 Standard"
};

export default Vue.component("VChart", {
  props: {
    items: VueTypes.array.isRequired,
    xAxisName: VueTypes.string.isRequired,
    yAxisName: VueTypes.string.isRequired,
    showTooltipX: VueTypes.bool.def(false),
    showTooltipY: VueTypes.bool.def(false),
    chartOptions: VueTypes.object
  },

  data() {
    return {
      dataMaxValue: getMaxValueFromArrayByKey(this.items, "total"),
      dataMinValue: getMinValueFromArrayByKey(this.items, "total")
    };
  },

  mounted() {
    this._initialize();
  },

  watch: {
    items: {
      immediate: true,
      handler(val) {
        this.dataMaxValue = getMaxValueFromArrayByKey(val, "total");
        this.dataMinValue = getMinValueFromArrayByKey(val, "total");
        this.$emit("maxchanged", this.dataMaxValue);
        this.$emit("minchanged", this.dataMinValue);
      }
    }
  },

  methods: {
    _initialize() {
      const chart = this._setupChartSettings();
      // setup x(date),y(value) axis
      this._setupAxisSettings(chart);
      this.series = this._setupSeries(chart);
      this.chart = chart;
    },

    _setupAxisSettings(chart) {
      this._setupXAxisSettings(chart);
      this._setupYAxisSettings(chart);
    },

    _setupAxisAdapters(axis, displayingCallback) {
      axis.renderer.labels.template.adapter.add("text", function(text, target) {
        const { value } = target.dataItem;

        if (!value) {
          return;
        }

        if (displayingCallback) {
          return displayingCallback(text, target);
        } else {
          return text;
        }
      });
    },

    _setupChartSettings() {
      let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

      // GENERAL CHART SETTINGS
      chart.width = am4core.percent(100);
      chart.height = am4core.percent(100);
      chart.hiddenState.properties.opacity = 0;
      chart.padding(10, 30, 0, 17);
      chart.zoomOutButton.disabled = true;
      chart.data = this.items;
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;
      chart.cursor.lineX.disabled = true;
      chart.cursor.behavior = "none";
      return chart;
    },

    _setupXAxisSettings(chart) {
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.location = 0;
      dateAxis.renderer.minGridDistance = 100;
      dateAxis.renderer.axisFills.template.disabled = true;
      dateAxis.renderer.grid.template.disabled = true;
      dateAxis.renderer.labels.template.fontSize = 14;
      dateAxis.renderer.labels.template.stroke = "#c5c5c5";
      dateAxis.renderer.labels.template.fontFamily = "Lato-Light";
      dateAxis.tooltip.disabled = true;
      dateAxis.startLocation = 0.5;
      dateAxis.endLocation = 0.5;
      dateAxis.dateFormats.setKey("day", "MMM d");
      dateAxis.periodChangeDateFormats.setKey("day", "MMM d");

      // setting up date axis adapters
      this._setupAxisAdapters(dateAxis, (text, target) => {
        const { index } = target.dataItem;

        // if (chart.data.length + 1 === index) {
        //   return "";
        // }

        return text;
      });
    },

    _setupYAxisSettings(chart) {
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.tooltip.disabled = true;
      valueAxis.renderer.minLabelPosition = 0;
      valueAxis.renderer.maxLabelPosition = 1;
      valueAxis.renderer.axisFills.template.disabled = true;
      valueAxis.renderer.ticks.template.disabled = true;
      valueAxis.renderer.grid.template.disabled = true;
      valueAxis.renderer.labels.template.fontSize = 14;
      valueAxis.renderer.labels.template.stroke = "#c5c5c5";
      valueAxis.maxPrecision = 0;
      valueAxis.equalSpacing = true;
      valueAxis.min = 0;

      // setting up values axis adapters
      this._setupAxisAdapters(valueAxis, (text, target) => {
        const { value } = target.dataItem;
        return value > this.dataMaxValue || value < 0 ? "" : text;
      });
    },

    _setupSeries(chart) {
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = "date";
      series.dataFields.valueY = "total";

      series.defaultState.transitionDuration = 0;
      series.tensionX = 0.8;

      // all the below is optional, makes some fancy effects
      // gradient fill of the series
      series.fillOpacity = 0.08;
      series.fill = am4core.color(variables.primary);
      series.stroke = am4core.color(variables.primary);
      series.strokeWidth = 1.2;
      series.bullets.push(new am4charts.CircleBullet());

      const shadow = this._createShadow();
      series.bullets.values.forEach(bullet => {
        bullet.circle.radius = 4.5;
        bullet.circle.fill = am4core.color("#fff");
        bullet.circle.strokeWidth = 4.5;
        bullet.filters.push(shadow);
      });

      // tooltip settings
      const dropShadow = this._createDropShadow();
      series.tooltip.background.cornerRadius = 5;
      series.tooltip.background.fill = am4core.color("#fff");
      series.tooltip.background.opacity = 1;
      series.tooltip.background.strokeWidth = 0;
      series.tooltip.background.filters.push(dropShadow);
      series.tooltip.background.fillOpacity = 1;
      series.tooltip.background.align = "middle";
      series.tooltip.width = 148;
      series.tooltip.minWidth = 148;
      series.tooltip.label.textAlign = "middle";
      series.tooltip.opacity = 1;
      series.tooltip.dy = -15;
      series.tooltip.fontFamily = "Lato-Light";
      series.tooltip.contentWidth = 148;
      series.tooltip.pixelPerfect = true;
      series.tooltip.pointerOrientation = "vertical";
      series.tooltip.getFillFromObject = false;
      series.tooltip.isMeasured = true;
      series.segments.template.interactionsEnabled = true;

      // event setting up
      if (this.dataMaxValue !== 0) {
        series.events.on("ready", e =>
          this._processOutSeries(e, chart, series)
        );

        chart.events.on("over", e => this._processOverSeries(e, chart, series));

        chart.events.on("out", e => this._processOutSeries(e, chart, series));
      } else {
        series.tooltipHTML = this._htmlString;
      }
    },

    _createShadow() {
      let shadow = new am4core.DropShadowFilter();
      shadow.dx = 0;
      shadow.dy = 0;
      shadow.opacity = 1;
      shadow.color = am4core.color(variables.primary);
      shadow.width = 600;
      shadow.height = 600;
      shadow.blur = 4;
      return shadow;
    },

    _createDropShadow() {
      // background
      const dropShadow = new am4core.DropShadowFilter();
      dropShadow.dx = 0;
      dropShadow.dy = 4;
      dropShadow.opacity = 0.25;
      return dropShadow;
    },

    /**
     * Callback for hover event
     * @param ev
     * @param chart
     * @param series
     * @private
     */
    _processOverSeries(ev, chart, series) {
      series.tooltipHTML = this._htmlString;

      chart.series.each(function(seriesItem) {
        seriesItem.dataItems.values.forEach(dataItem => {
          dataItem.bullets.each((key, val) => {
            // if (dataItem.valueY !== 0) {
            val.opacity = 1;
            // }
          });
        });
      });
    },

    /**
     * Callback for blur event
     * @param ev
     * @param chart
     * @param series
     * @private
     */
    _processOutSeries(ev, chart, series) {
      series.tooltipHTML = "";

      chart.series.each(function(seriesItem) {
        seriesItem.dataItems.values.forEach(dataItem => {
          dataItem.bullets.each((key, val) => (val.opacity = 0));
        });
      });
    }
  },

  computed: {
    _htmlString() {
      // generates one row for displaying particular statistics info per type
      const _generateMailpieceInfoRow = type =>
        `<div style="font-family: 'Lato-Light', sans-serif; font-size: 14px; color: ${variables.secondaryBorderColor};
            margin-bottom: 10px; text-align: left;"> ${LABELS_MAP[type]}: {${type}} </div>`;

      return `
          <div style="width: 100%; display: flex; flex-direction: column; margin-bottom:6px;">
           <div style="font-size: 12px; margin-bottom: 13px; text-transform: capitalize; font-family: 'Lato-Regular', sans-serif; color: ${
             variables.primaryTextColor
           };"> {date.formatDate('MMM d')} </div>
            <div style="display: flex; align-items: flex-start">

            <div style="margin-right: 22px" >
               <div style="font-size: 10px; text-align: left; margin-bottom: 12px; font-family: 'Lato-Bold', sans-serif; color: ${
                 variables.primaryTextColor
               };">Postcards</div>
               ${_generateMailpieceInfoRow("4x6-usps-first-class")}
               ${_generateMailpieceInfoRow("4x6-usps-standard")}
               ${_generateMailpieceInfoRow("6x9-usps-first-class")}
               ${_generateMailpieceInfoRow("6x9-usps-standard")}
               ${_generateMailpieceInfoRow("6x11-usps-first-class")}
               ${_generateMailpieceInfoRow("6x11-usps-standard")}
               </div>

               <div>
               <div style="font-size: 10px; text-align: left; margin-bottom: 12px; font-family: 'Lato-Bold', sans-serif; color: ${
                 variables.primaryTextColor
               };">Letters</div>
               ${_generateMailpieceInfoRow("b&w-usps-first-class")}
               ${_generateMailpieceInfoRow("b&w-usps-standard")}
               ${_generateMailpieceInfoRow("color-usps-first-class")}
               ${_generateMailpieceInfoRow("color-usps-standard")}
               </div>
             </div>

          <div style="position: relative; width: 100%; min-height: 8px;">
             <div style="position: absolute; letter-spacing: 1.2px; top: 50%; transform: translateY(-50%); width: 100%; border-bottom: thin #c5c5c5 dashed;"></div>
          </div>

          <div style="margin-top: 13px; display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${
                variables.secondaryBorderColor
              }; font-size: 14px;"> Total: </span> <span style="color: ${
        variables.primary
      }; font-family: 'Lato-Bold', sans-serif; font-size: 14px;">${`$\{total_cost.formatNumber('#.00')}`}</span>
          </div>
        </div>
      `;
    }
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }
});
</script>

<style lang="scss" scoped>
.v-chart {
  width: 100%;
  height: calc(100vh - 300px);

  @media screen and (max-width: 767px) {
    height: 500px;
  }
}
</style>
